/**
 * Tiny DOM helpers shared by all UI modules (no framework).
 *
 *   const btn = h('button', { class: 'bm-btn', onClick: () => …, 'aria-label': 'Play' }, '▶ Play');
 *
 * Conventions used across `src/ui`:
 * - Every component exports `mount<Name>(container: HTMLElement, deps): { el: HTMLElement; dispose(): void }`.
 * - Components never keep their own copy of session state; they subscribe to the
 *   store and flip attributes (`data-on`, `data-by`, `data-vel`) on a DOM built once.
 * - CSS lives next to the component (`sequencer.css`) and is imported by the module.
 * - Class names are prefixed `bm-`.
 */

export type Child = Node | string | number | null | undefined | false | Child[];

export interface Props {
  [key: string]: unknown;
  class?: string;
  style?: string | Partial<CSSStyleDeclaration>;
  dataset?: Record<string, string | number | boolean | undefined>;
  ref?: (el: HTMLElement) => void;
}

function append(parent: Node, child: Child): void {
  if (child == null || child === false) return;
  if (Array.isArray(child)) {
    for (const c of child) append(parent, c);
    return;
  }
  parent.appendChild(child instanceof Node ? child : document.createTextNode(String(child)));
}

/** Create an element with props (attributes, `onEvent` handlers, `class`, `style`, `dataset`) and children. */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Props | null = null,
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (props) {
    for (const [key, value] of Object.entries(props)) {
      if (value == null || value === false) continue;
      if (key === 'class') el.className = String(value);
      else if (key === 'style') {
        if (typeof value === 'string') el.setAttribute('style', value);
        else Object.assign(el.style, value);
      } else if (key === 'dataset') {
        for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
          if (v != null) el.dataset[k] = String(v);
        }
      } else if (key === 'ref') (value as (el: HTMLElement) => void)(el);
      else if (key.startsWith('on') && typeof value === 'function') {
        el.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
      } else if (value === true) el.setAttribute(key, '');
      else el.setAttribute(key, String(value));
    }
  }
  for (const c of children) append(el, c);
  return el;
}

/** Inject a stylesheet once per id (components call this at module load). */
export function injectStyle(id: string, css: string): void {
  if (document.getElementById(id)) return;
  const s = document.createElement('style');
  s.id = id;
  s.textContent = css;
  document.head.appendChild(s);
}

/** Replace all children of `el` with `children`. */
export function setChildren(el: HTMLElement, ...children: Child[]): void {
  el.replaceChildren();
  for (const c of children) append(el, c);
}

/** Toggle a boolean attribute (`data-on`, `aria-pressed`) without re-rendering. */
export function setFlag(el: HTMLElement, name: string, on: boolean, value = ''): void {
  if (on) el.setAttribute(name, value);
  else el.removeAttribute(name);
}

/** Format seconds ago as "12s" / "3m" for `human.recent`. */
export function ago(atMs: number, nowMs = Date.now()): string {
  const s = Math.max(0, Math.round((nowMs - atMs) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.round(m / 60)}h`;
}

/** Note name for a MIDI pitch using sharps ("C4"). Duplicated from theory to keep the UI dependency-free. */
export function pitchLabel(pitch: number): string {
  const names = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  return `${names[((pitch % 12) + 12) % 12]}${Math.floor(pitch / 12) - 1}`;
}

/** True when the user prefers reduced motion. */
export function reducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}
